import { TableRowSideSheetForm } from "../../../common/tables/view/TableRowSideSheetForm";
import React, {
    ChangeEvent,
    FC,
    PropsWithChildren,
    useMemo,
} from "react";
import { CostPayoutModifierCreateInput } from "../../../../../common/inputs/CostPayoutModifierInput";
import { isValidEmailDomain, normalizeCountryCode, isValidCountryCode, parseEmailDomainInput } from "../../../../../common/utils/inputValidation";
import {
    TextInputField,
} from "evergreen-ui";
import { LabelledSwitch } from "../../../common/LabelledSwitch";
import { LabelledTooltip } from "../../../common/LabelledTooltip";

interface Props {
    isShown: boolean;
    costPayoutModifier: Partial<CostPayoutModifierCreateInput>;
    mutationLoading: boolean;
    mutationCalled: boolean;
    mutationError: boolean;
    onInputChanged: (costPayoutModifier: Partial<CostPayoutModifierCreateInput>) => void;
    onDismiss: () => void;
    onSubmit: () => void;
}

export const CPMTableRowSideSheetForm: FC<Props> = ({
    isShown,
    costPayoutModifier,
    mutationLoading,
    mutationCalled,
    mutationError,
    onInputChanged,
    onDismiss,
    onSubmit,
}: PropsWithChildren<Props>) => {
    const onChange = ({ target: { value, name, type } }: ChangeEvent<HTMLInputElement>) => {
        onInputChanged({
            ...costPayoutModifier,
            [name]: (type === "number")
                ? parseInt(value, 10)
                : (name === "conditionCountryCode") ? value.toUpperCase()
                : (name === "conditionEmailDomains") ? value.toLowerCase()
                : value,
        });
    };

    const hasInvalidCountryCode = useMemo((): boolean => {
        const cc = normalizeCountryCode(costPayoutModifier.conditionCountryCode);
        return cc !== "" && !isValidCountryCode(cc);
    }, [costPayoutModifier.conditionCountryCode]);

    const hasInvalidEmailDomains = useMemo((): boolean => {
        const raw = costPayoutModifier.conditionEmailDomains;
        if (raw === undefined) return false;
        const entries = Array.isArray(raw)
            ? parseEmailDomainInput(raw.join(","))
            : parseEmailDomainInput(raw as unknown as string);
        if (entries.length === 0) return false;
        return entries.some((d: string) => !isValidEmailDomain(d));
    }, [costPayoutModifier.conditionEmailDomains]);

    const canSubmit = useMemo((): boolean => {
        const nameValid = costPayoutModifier.name !== undefined && costPayoutModifier.name.trim() !== "";
        return nameValid && !hasInvalidCountryCode && !hasInvalidEmailDomains;
    }, [costPayoutModifier.name, hasInvalidCountryCode, hasInvalidEmailDomains]);

    return (
        <TableRowSideSheetForm
            isShown={isShown}
            headerTitle={"Add Modifier"}
            mutationLoading={mutationLoading}
            mutationCalled={mutationCalled}
            mutationError={mutationError}
            canSubmit={canSubmit}
            onDismiss={onDismiss}
            onSubmit={onSubmit}
        >
            <TextInputField
                name={"name"}
                label={"Name"}
                value={costPayoutModifier.name ?? ""}
                required={true}
                maxWidth={"300px"}
                isInvalid={costPayoutModifier.name !== undefined && costPayoutModifier.name.trim() === ""}
                onChange={onChange}
            />
            <TextInputField
                name={"conditionCountryCode"}
                description={(
                    <LabelledTooltip
                        label={"Country Code"}
                        description={"2-character code (e.g. US, CA)"}
                    />
                )}
                value={costPayoutModifier.conditionCountryCode ?? ""}
                maxWidth={"300px"}
                maxLength={2}
                isInvalid={hasInvalidCountryCode}
                onChange={onChange}
            />
            <TextInputField
                name={"conditionEmailDomains"}
                description={(
                    <LabelledTooltip
                        label={"Email Domains"}
                        description={"Comma-separated list of email addresses or domains"}
                    />
                )}
                value={
                    Array.isArray(costPayoutModifier.conditionEmailDomains)
                        ? costPayoutModifier.conditionEmailDomains.join(", ")
                        : (costPayoutModifier.conditionEmailDomains ?? "")
                }
                maxWidth={"300px"}
                isInvalid={hasInvalidEmailDomains}
                onChange={onChange}
            />
            <TextInputField
                name={"conditionSourceName"}
                description={(
                    <LabelledTooltip
                        label={"Source Name"}
                        description={"Order source name filter (e.g. web, pos)"}
                    />
                )}
                value={costPayoutModifier.conditionSourceName ?? ""}
                maxWidth={"300px"}
                onChange={onChange}
            />
            <TextInputField
                name={"conditionFulfillmentService"}
                description={(
                    <LabelledTooltip
                        label={"Fulfillment Service"}
                        description={"Fulfillment service filter (e.g. flexport, manual)"}
                    />
                )}
                value={costPayoutModifier.conditionFulfillmentService ?? ""}
                maxWidth={"300px"}
                onChange={onChange}
            />
            <LabelledSwitch
                description={(
                    <LabelledTooltip
                        label={"Bundled Item"}
                        description={"Matches only line items that are part of a bundle"}
                    />
                )}
                checked={costPayoutModifier.conditionIsBundle === true}
                onChange={() => {
                    onInputChanged({
                        ...costPayoutModifier,
                        conditionIsBundle: (costPayoutModifier.conditionIsBundle === true)
                            ? undefined
                            : true,
                    });
                }}
            />
            <TextInputField
                name={"priority"}
                description={(
                    <LabelledTooltip
                        label={"Priority"}
                        description={"Higher values take precedence when multiple modifiers match"}
                    />
                )}
                type={"number"}
                step={1}
                value={costPayoutModifier.priority ?? 0}
                maxWidth={"300px"}
                onChange={onChange}
            />
        </TableRowSideSheetForm>
    );
};
